import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useBranch } from "@/context/BranchContext";
import { roleMeta } from "@/config/roles";

export default function AccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { employee, logout } = useBranch();
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const state = location.state as { toast?: string } | null;
    if (state?.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!employee) {
    return <Navigate to="/" replace />;
  }

  const role = roleMeta(employee.role);

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-6">
      {/* Profile card */}
      <div className="bg-background-50 rounded-card border border-background-200 p-5 md:p-6 animate-fade-slide-up">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-3xl font-bold shrink-0">
            {employee.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-foreground-900">{employee.name}</h2>
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${role.badgeClass}`}
              >
                <i className={role.icon}></i>
                {role.label}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary-100 text-primary-700">
                <i className="ri-checkbox-circle-line"></i>
                الحساب نشط
              </span>
            </div>
            <p className="mt-1.5 text-sm text-foreground-500">
              {employee.storeName} — {employee.branchName}
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="self-start sm:self-auto inline-flex items-center gap-2 px-5 h-11 rounded-btn bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line text-lg"></i>
            تسجيل الخروج
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-background-200 pt-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-btn bg-background-200 flex items-center justify-center">
              <i className="ri-smartphone-line text-foreground-600 text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-foreground-500">رقم الجوال</p>
              <p className="text-sm font-semibold text-foreground-900" dir="ltr">{employee.phone}</p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/account/change-phone")}
              className="h-10 px-3.5 rounded-btn border border-background-300 text-xs font-bold text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line ml-1"></i>
              تغيير
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-btn bg-background-200 flex items-center justify-center">
              <i className="ri-map-pin-line text-foreground-600 text-lg"></i>
            </div>
            <div>
              <p className="text-xs text-foreground-500">الفرع</p>
              <p className="text-sm font-semibold text-foreground-900">{employee.branchName}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="bg-background-50 rounded-card border border-background-200 p-5 flex items-center gap-4 animate-fade-slide-up">
        <div className="w-11 h-11 rounded-btn bg-accent-100 text-accent-900 flex items-center justify-center shrink-0">
          <i className="ri-customer-service-2-line text-xl"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground-900">تحتاج مساعدة؟</p>
          <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
            لأي مشكلة في الحساب أو الصلاحيات، تواصل مع صاحب المحل أو الدعم الفني.
          </p>
        </div>
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] flex items-center gap-2.5 px-4 py-3 rounded-btn bg-foreground-900 text-white animate-fade-slide-up">
          <i className="ri-checkbox-circle-line text-primary-400 text-lg"></i>
          <span className="text-sm font-semibold whitespace-nowrap">{toast}</span>
        </div>
      ) : null}
    </div>
  );
}